import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Download, ShieldCheck, Star, Twitter, Facebook, Linkedin, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [ratingAvg, setRatingAvg] = useState(0);
  const [ratingCount, setRatingCount] = useState(0);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = () => {
    api.get(`/products/${id}/reviews`).then((res) => {
      setReviews(res.data.reviews);
      setRatingAvg(res.data.average);
      setRatingCount(res.data.count);
    });
  };

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((res) => setProduct(res.data))
      .catch(() => setProduct(false))
      .finally(() => setLoading(false));
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (product && product.title) document.title = `${product.title} — LuxDrop`;
    return () => { document.title = "LuxDrop — Digital Products for Freelancers"; };
  }, [product]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!myRating) { toast.error("Please pick a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post(`/products/${id}/reviews`, { rating: myRating, comment: myComment });
      toast.success("Thanks for your review!");
      setMyRating(0); setMyComment("");
      loadReviews();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="animate-pulse font-serif text-2xl tracking-tighter">Loading…</div>
      </div>
    );

  if (!product)
    return (
      <div className="mx-auto max-w-7xl px-5 py-24 text-center md:px-8">
        <h2 className="font-serif text-3xl tracking-tighter">Product not found</h2>
        <Link to="/" className="mt-4 inline-block text-signal underline">Back to shop</Link>
      </div>
    );

  const handleAdd = () => {
    addItem(product);
    toast.success(`${product.title} added to cart`);
  };

  const handleBuyNow = () => {
    addItem(product);
    navigate("/cart");
  };

  return (
    <div data-testid="product-detail-page" className="mx-auto max-w-7xl px-5 py-10 md:px-8">
      <Link to="/" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-obsidian/60 transition-colors hover:text-signal">
        <ArrowLeft className="h-4 w-4" /> Back to shop
      </Link>

      <div className="mt-8 grid gap-12 md:grid-cols-2">
        <div className="space-y-6">
          <div className="overflow-hidden border border-obsidian/15">
            <img src={product.image} alt={product.title} className="h-full w-full object-cover" />
          </div>
          <div className="border border-obsidian/15 bg-white p-6">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian/50">
              About this product
            </h3>
            <p className="mt-3 text-base leading-relaxed text-obsidian/80">{product.description}</p>
          </div>
        </div>

        <div>
          <div className="sticky top-28 border border-obsidian/15 bg-white p-8">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-signal">
              {product.category}
            </span>
            <h1 className="mt-3 font-serif text-4xl font-light leading-none tracking-tighter text-obsidian md:text-5xl">
              {product.title}
            </h1>
            <p className="mt-3 text-base text-obsidian/60">{product.tagline}</p>

            <div className="mt-5 flex items-center gap-1 text-signal">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`h-4 w-4 ${i < Math.round(ratingAvg) ? "fill-signal" : "fill-none text-obsidian/25"}`} />
              ))}
              <span className="ml-2 text-xs uppercase tracking-[0.15em] text-obsidian/50">
                {ratingCount > 0 ? `${ratingAvg} · ${ratingCount} review${ratingCount > 1 ? "s" : ""}` : "No reviews yet"}
              </span>
            </div>

            <div className="mt-6 flex items-end gap-3 border-y border-obsidian/15 py-6">
              <span className="font-serif text-5xl tracking-tighter text-obsidian">${product.price}</span>
              <span className="mb-1.5 text-xs uppercase tracking-[0.15em] text-obsidian/40">
                one-time · {product.file_format}
              </span>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <button
                data-testid="buy-now-btn"
                onClick={handleBuyNow}
                className="w-full bg-signal py-4 text-sm font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-red-700"
              >
                Buy now
              </button>
              <button
                data-testid="add-to-cart-btn"
                onClick={handleAdd}
                className="w-full border border-obsidian bg-transparent py-4 text-sm font-semibold uppercase tracking-[0.2em] text-obsidian transition-colors hover:bg-obsidian hover:text-white"
              >
                Add to cart
              </button>
            </div>

            <ul className="mt-6 space-y-2 text-sm text-obsidian/70">
              <li className="flex items-center gap-2"><Download className="h-4 w-4 text-signal" /> Instant digital download</li>
              <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-signal" /> Lifetime access & updates</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-signal" /> Commercial license included</li>
            </ul>

            <div className="mt-6 flex items-center gap-3 border-t border-obsidian/10 pt-5">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-obsidian/40">Share</span>
              {(() => {
                const url = typeof window !== "undefined" ? window.location.href : "";
                const text = `Check out ${product.title} on LuxDrop`;
                const links = [
                  { icon: Twitter, testid: "share-twitter", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}` },
                  { icon: Facebook, testid: "share-facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
                  { icon: Linkedin, testid: "share-linkedin", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
                ];
                return (
                  <>
                    {links.map((l) => (
                      <a key={l.testid} data-testid={l.testid} href={l.href} target="_blank" rel="noopener noreferrer"
                        className="border border-obsidian/20 p-2 text-obsidian/70 transition-colors hover:border-signal hover:text-signal">
                        <l.icon className="h-4 w-4" />
                      </a>
                    ))}
                    <button data-testid="share-copy" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }}
                      className="border border-obsidian/20 p-2 text-obsidian/70 transition-colors hover:border-signal hover:text-signal">
                      <Link2 className="h-4 w-4" />
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <div data-testid="reviews-section" className="mt-16 border-t border-obsidian/15 pt-10">
        <h2 className="font-serif text-3xl font-light tracking-tight text-obsidian">
          Reviews {ratingCount > 0 && <span className="text-obsidian/40">({ratingCount})</span>}
        </h2>

        <div className="mt-8 grid gap-10 md:grid-cols-3">
          {/* Write a review */}
          <div className="md:col-span-1">
            <div className="border border-obsidian/15 bg-white p-6">
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian/50">Write a review</h3>
              {user ? (
                <form onSubmit={submitReview} className="mt-4">
                  <div className="flex gap-1" data-testid="review-stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        type="button"
                        key={n}
                        data-testid={`review-star-${n}`}
                        onClick={() => setMyRating(n)}
                        className="text-signal transition-transform hover:scale-110"
                      >
                        <Star className={`h-6 w-6 ${n <= myRating ? "fill-signal" : "fill-none text-obsidian/25"}`} />
                      </button>
                    ))}
                  </div>
                  <textarea
                    data-testid="review-comment"
                    value={myComment}
                    onChange={(e) => setMyComment(e.target.value)}
                    rows={4}
                    placeholder="Share what you thought…"
                    className="mt-4 w-full border border-obsidian/20 bg-bone px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-obsidian focus:ring-offset-1"
                  />
                  <button
                    data-testid="review-submit"
                    type="submit"
                    disabled={submitting}
                    className="mt-3 w-full bg-signal py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-red-700 disabled:opacity-60"
                  >
                    {submitting ? "Posting…" : "Post review"}
                  </button>
                </form>
              ) : (
                <p className="mt-4 text-sm text-obsidian/60">
                  <Link to="/login" className="font-semibold text-signal hover:underline">Sign in</Link> to write a review.
                </p>
              )}
            </div>
          </div>

          {/* Review list */}
          <div className="md:col-span-2">
            {reviews.length === 0 ? (
              <div className="border border-dashed border-obsidian/20 p-10 text-center text-obsidian/50">
                Be the first to review this product.
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((r) => (
                  <div key={r.id} data-testid={`review-${r.id}`} className="border border-obsidian/15 bg-white p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-signal">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-signal" : "fill-none text-obsidian/25"}`} />
                        ))}
                      </div>
                      {r.verified && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-obsidian/50">
                          <Check className="h-3 w-3 text-signal" /> Verified buyer
                        </span>
                      )}
                    </div>
                    {r.comment && <p className="mt-3 text-sm leading-relaxed text-obsidian/80">{r.comment}</p>}
                    <p className="mt-3 text-xs uppercase tracking-[0.15em] text-obsidian/40">
                      {r.author} · {new Date(r.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
